import React from "react";
import { Lock, ShieldCheck, DollarSign, BadgeCheck, Users } from "lucide-react";
import { PageHero, Section, Container, Eyebrow, H2, Lead, CTAButton, GlassCard, CompareTable, Orbs } from "../components/PageUI";
import { compareRows } from "../mock";
import SEO from "../components/SEO";

const security = [
  "Secure payment processing powered by trusted payment infrastructure through Stripe",
  "PCI-compliant payment handling to protect donor transactions",
  "Card details are never stored on Masjid Suite servers",
  "Designed with privacy and data protection as a core priority",
];

export default function WhyUs() {
  return (
    <div>
      <SEO
        title="Why Masjid Suite | Built Exclusively for Masjids"
        description="See why masjids choose Masjid Suite over generic nonprofit software: secure Stripe payments, no per-feature pricing, and a platform designed for Islamic communities."
        path="/why-us"
      />
      <PageHero
        eyebrow="Why Masjid Suite"
        title="Modern. Simple. Secure."
        gradient="Built Exclusively for Masjids."
        lead={<>Masjid Suite is a next-generation platform designed specifically for the needs of modern Islamic communities. Unlike generic nonprofit software that is modified to fit religious organizations, Masjid Suite was built from the ground up for masjids, combining intuitive design, powerful features, and a seamless user experience for administrators, Imams, staff, and community members.</>}
      />

      <Section className="bg-section-alt overflow-hidden">
        <Container>
          <div className="text-center mb-14 max-w-4xl mx-auto">
            <Lead>Our modern, mobile-first platform makes managing your masjid easier than ever. From announcements and events to donations, memberships, and daily operations, Masjid Suite replaces outdated tools and complicated workflows with a simple, user-friendly experience that anyone can use.</Lead>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <GlassCard hover={false}>
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#1e4218] to-[#0e2408] text-white flex items-center justify-center mb-4"><ShieldCheck size={22}/></div>
              <h3 className="font-display text-xl text-[#0e2408] font-semibold mb-3">Built for Security</h3>
              <p className="text-[14px] text-[#4b5563] mb-4">Protect your community’s information and donations with enterprise-grade security.</p>
              <ul className="space-y-3 text-[13px] text-[#4b5563]">
                {security.map((t, i) => (
                  <li key={i} className="flex items-start gap-2"><BadgeCheck size={14} className="text-[#0e2408] mt-0.5 shrink-0"/> <span>{t}</span></li>
                ))}
              </ul>
            </GlassCard>

            <GlassCard hover={false}>
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#dcbb6a] to-[#a8862f] text-white flex items-center justify-center mb-4"><DollarSign size={22}/></div>
              <h3 className="font-display text-xl text-[#0e2408] font-semibold mb-3">Affordable by Design</h3>
              <p className="text-[14px] text-[#4b5563] mb-3">Technology for your masjid shouldn’t come with expensive monthly fees.</p>
              <p className="text-[14px] text-[#4b5563] leading-relaxed">Masjid Suite eliminates costly software subscriptions by providing the platform at no recurring software cost. Your masjid only pays for the hardware or optional services it chooses to use, with no hidden fees, no long-term commitments, and no unnecessary expenses.</p>
            </GlassCard>

            <GlassCard hover={false}>
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#1e4218] to-[#0e2408] text-white flex items-center justify-center mb-4"><Users size={22}/></div>
              <h3 className="font-display text-xl text-[#0e2408] font-semibold mb-3">A Better Experience for Your Entire Community</h3>
              <p className="text-[14px] text-[#4b5563] leading-relaxed">Masjid Suite helps your team save time while giving members a more convenient way to stay connected, donate, participate, and engage. With a modern interface built for today’s mobile-first communities, Masjid Suite delivers a simpler, smarter, and more effective way to manage your masjid.</p>
            </GlassCard>
          </div>
        </Container>
      </Section>

      <Section className="bg-hero-light overflow-hidden">
        <Orbs />
        <Container>
          <div className="text-center mb-14">
            <H2 gradient="Masjid Suite?">Why</H2>
            <div className="mt-6 flex justify-center"><Lead>See how Masjid Suite compares to traditional Masjid software.</Lead></div>
          </div>
          <CompareTable rows={compareRows} />
        </Container>
      </Section>

      <Section id="how-we-stay-free" className="bg-section-alt overflow-hidden">
        <Container>
          <GlassCard hover={false} className="p-10 md:p-14">
            <Eyebrow>How Masjid Suite stays free</Eyebrow>
            <H2 gradient="honest way">A transparent,</H2>
            <div className="mt-6 space-y-5 text-[15px] text-[#4b5563] leading-relaxed max-w-4xl">
              <p>Masjid Suite is committed to providing powerful, modern technology to masjids without the burden of monthly software subscription fees. We are able to offer our full platform at no cost by partnering with carefully selected local halal businesses that support the growth and digital transformation of Islamic communities.</p>
              <p>These trusted partners may appear through modest, non-intrusive placements within the app or on kiosk idle screens, helping fund the platform while keeping the experience clean and respectful for users.</p>
              <p>Every sponsor and business partner is carefully reviewed to ensure alignment with Islamic values and Shariah compliance before being presented to your community. Masjids remain in control and have the ability to reject any sponsor, advertisement, or business partnership that does not align with their principles or community standards.</p>
              <p>Through this model, Masjid Suite creates a sustainable way for masjids to access modern technology while maintaining trust, transparency, and control.</p>
            </div>
            <div className="mt-6 inline-flex items-center gap-2 text-[#0e2408] text-[13px] font-medium"><Lock size={14}/> Shariah-reviewed · Opt-out any time</div>
          </GlassCard>
        </Container>
      </Section>

      <Section className="bg-hero-light overflow-hidden">
        <Orbs />
        <Container>
          <div className="glass-frosted rounded-[40px] p-10 md:p-16 text-center shadow-apple-lg">
            <H2 gradient="built just for it.">Give your masjid a platform</H2>
            <div className="mt-10 flex justify-center"><CTAButton to="/contact">Get Started Free</CTAButton></div>
          </div>
        </Container>
      </Section>
    </div>
  );
}
